<script setup lang="ts">
  import type { ImageType } from '~/graphql/misc';
  import { onMounted, ref, watch, onBeforeUnmount, computed, nextTick } from 'vue';
  import Splide from '@splidejs/splide';

  const props = defineProps<{
      imageSlides: {
        image: ImageType,
        caption: string
      }[],
      slideId?: string
  }>();
  
  const splideEl = ref<HTMLElement | null>(null);
  let splideInstance: Splide | null = null;
  
  const slides = computed(() => props?.imageSlides || []);

  const destroySplide = () => {
    if (splideInstance) {
      splideInstance.destroy();
      splideInstance = null;
    }
  };

  const initializeSplide = () => {
    if (!splideEl.value || !slides.value.length) return;

    destroySplide(); // Clean up existing instance

    var slideOptions = {
      type   : 'loop',
      padding: '18rem',
      pagination: false,
      lazyLoad: 'nearby',
      autoplay: true,
      breakpoints: {
        998: {
          padding: '0rem',
          arrows: false
        }
      },
      interval: 10000
    }


    if (props?.imageSlides?.length < 2) {
      slideOptions.type = 'slide';
      slideOptions.arrows = false;
    }
    

    splideInstance = new Splide(splideEl.value, slideOptions);
    
    // Store all caption references upfront when DOM is ready
    let allCaptions = Array.from(document.querySelectorAll('.slide-caption'))
    let previousIndex = 0
    
    splideInstance.on('ready', () => {
      const initialCaption = allCaptions[splideInstance!.index];
      if (initialCaption) {
        initialCaption.style.opacity = '0.8';
      }
    });

    // Track the previous slide and hide its caption instantly on move
    splideInstance.on('move', (newIndex) => {
      
      // Hide the previous slide's caption instantly
      const previousCaption = allCaptions[previousIndex];
      if (previousCaption) {
        previousCaption.style.transition = 'none';
        previousCaption.style.opacity = '0';
        previousCaption.style.transition = 'opacity 0.1s ease-in-out !important';
      }

      const nextCaption = allCaptions[newIndex];
      if (nextCaption) {
        nextCaption.style.transition = 'opacity 0.5s ease-in-out';
        nextCaption.style.opacity = '0.8';
      }

      // Update the previous index for next time
      previousIndex = newIndex
    })
    splideInstance.mount();
  };

  // Watch for changes to imageSlides and reinitialize
  watch(() => props.imageSlides, () => {
    nextTick(() => {
      initializeSplide();
    });
  }, { deep: true });
  
  onMounted(() => {
    nextTick(() => {
      initializeSplide();
    });
  });

  onBeforeUnmount(() => {
    destroySplide();
  });
</script>
<template>
    <div :id="slideId || 'slider-div'" class="splide custom-splide slider-section-div" ref="splideEl">
      <div class="splide__arrows">
        <button class="splide__arrow splide__arrow--prev">
          <NuxtImg loading="lazy"
            alt="Previous"
            quality="80"
            height="50" width="50"
            src="/left-arrow.svg"/>
        </button>
        <button class="splide__arrow splide__arrow--next">
          <NuxtImg loading="lazy"
            alt="Next"
            quality="80"
            height="50" width="50"
            src="/right-arrow.svg"/>
        </button>
      </div>
      <div class="splide__track">
        <ul class="splide__list">
          <li
            v-for="(slide, i) in slides"
            :key="i"
            class="splide__slide"
          >
            <div class="slide-wrapper">
              <NuxtImg v-if="slide?.image?.node?.mediaItemUrl" :src="slide?.image?.node?.mediaItemUrl" alt="Slide image" loading="lazy" format="webp" :srcset="slide?.image?.node?.srcSet" :quality="80"/>
              <div class="slide-caption">
                <div v-html="slide.caption"></div>
                <span class="page-number">{{ i + 1 }}/{{ slides.length }}</span>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
</template>
